import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", role: "", company: "", quote: "", rating: 5, active: true };

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!form.name || !form.quote) { toast.error("Name and quote are required"); return; }
    await api.post("/admin/testimonials", { ...form, rating: Number(form.rating) || 5 });
    toast.success("Testimonial added");
    setOpen(false);
    setForm(EMPTY);
    load();
  };

  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-forest">Testimonials</h1>
        <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-testimonial-btn" className="rounded-full bg-forest hover:bg-olive text-cream transition-colors"><Plus size={16} className="mr-2" /> Add</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl border border-forest/10 p-5" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex gap-1 mb-3">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={14} className="text-gold" fill="currentColor" />)}</div>
            <p className="text-sm text-forest/80 italic">{`"${t.quote}"`}</p>
            <div className="flex items-center justify-between mt-4">
              <div>
                <p className="text-sm font-medium text-forest">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.role}{t.company ? `, ${t.company}` : ""}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => del(t.id)} data-testid={`delete-testimonial-${t.id}`} className="text-red-600"><Trash2 size={16} /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream max-w-lg" data-testid="testimonial-editor-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-forest">Add Testimonial</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-forest">Name</Label><Input value={form.name} onChange={set("name")} data-testid="testimonial-name" className="border-forest/15" /></div>
              <div className="space-y-1.5"><Label className="text-forest">Rating (1-5)</Label><Input type="number" min={1} max={5} value={form.rating} onChange={set("rating")} data-testid="testimonial-rating" className="border-forest/15" /></div>
              <div className="space-y-1.5"><Label className="text-forest">Role</Label><Input value={form.role} onChange={set("role")} data-testid="testimonial-role" className="border-forest/15" /></div>
              <div className="space-y-1.5"><Label className="text-forest">Company</Label><Input value={form.company} onChange={set("company")} data-testid="testimonial-company" className="border-forest/15" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-forest">Quote</Label><Textarea rows={3} value={form.quote} onChange={set("quote")} data-testid="testimonial-quote" className="border-forest/15" /></div>
            <Button onClick={save} data-testid="save-testimonial" className="w-full rounded-full bg-forest hover:bg-olive text-cream transition-colors">Add Testimonial</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
